'use client'
import { ReactNode, useLayoutEffect, useRef } from 'react'

export default function ColorProvider({
	children,
	color = 'dark',
}: {
	children: ReactNode
	color?: 'dark' | 'light'
}) {
	const scrollTimeoutRef = useRef<NodeJS.Timeout>()
	const headerRef = useRef<HTMLElement | null>(null)
	const searchBarRef = useRef<HTMLElement | null>(null)
	const lastScrollTopRef = useRef<number>(0)

	const updateHeaderBackground = (scrollTop: number) => {
		if (!headerRef.current) {
			headerRef.current = document.querySelector(
				'.header-scroll',
			) as HTMLElement
		}

		if (!headerRef.current) {
			return
		}

		// Calculate opacity based on scroll position
		// Start transition at 0px, fully opaque at 100px
		const maxScrollForTransition = 100
		const opacity = Math.min(scrollTop / maxScrollForTransition, 1)

		// Set custom CSS variable for smooth opacity transition
		headerRef.current.style.setProperty(
			'--header-bg-opacity',
			opacity.toString(),
		)

		// Apply background with calculated opacity using CSS custom property
		if (opacity > 0) {
			headerRef.current.classList.add('bg-background')
			headerRef.current.classList.remove('dark:bg-gray-900')
		} else {
			headerRef.current.classList.remove('bg-background')
		}
	}

	useLayoutEffect(() => {
		const header = document.querySelector('header')
		if (!header) return

		if (color === 'light') header.classList.remove('dark')
		else header.classList.add('dark')
		// @ts-ignore
		document.activeElement.blur()

		const mobileToggleMenu = document.querySelector(
			'#dropdown-mobile-header-menu',
		)
		if (mobileToggleMenu) {
			// @ts-ignore
			mobileToggleMenu.checked = false
		}

		// Cache DOM elements
		headerRef.current = document.querySelector('.header-scroll') as HTMLElement
		searchBarRef.current = document.querySelector('#search-bar') as HTMLElement

		// Initialize header state
		const { scrollTop } = document.documentElement
		lastScrollTopRef.current = scrollTop
		updateHeaderBackground(scrollTop)

		// Throttled scroll handler for smooth performance
		const handleScroll = () => {
			if (scrollTimeoutRef.current) {
				clearTimeout(scrollTimeoutRef.current)
			}

			scrollTimeoutRef.current = setTimeout(() => {
				requestAnimationFrame(() => {
					const { scrollTop } = document.documentElement
					// If scrollTop is 0 but we were previously scrolled, use the last known position
					// This handles the case where scrolling is blocked by a drawer
					const effectiveScrollTop =
						scrollTop === 0 && lastScrollTopRef.current > 0
							? lastScrollTopRef.current
							: scrollTop

					lastScrollTopRef.current = scrollTop
					updateHeaderBackground(effectiveScrollTop)
				})
			}, 10) // 10ms throttle for smooth 60fps updates
		}

		// Add scroll event listener
		document.addEventListener('scroll', handleScroll, { passive: true })

		// Mobile menu handler
		if (mobileToggleMenu) {
			mobileToggleMenu.addEventListener('change', function () {
				if (!searchBarRef.current) {
					searchBarRef.current = document.querySelector(
						'#search-bar',
					) as HTMLElement
				}

				if (this.checked) {
					searchBarRef.current?.classList.add('dark')
				} else {
					searchBarRef.current?.classList.remove('dark')
				}
			})
		}

		// Cleanup function
		return () => {
			document.removeEventListener('scroll', handleScroll)
			if (scrollTimeoutRef.current) {
				clearTimeout(scrollTimeoutRef.current)
			}
			// Clear cached refs
			headerRef.current = null
			searchBarRef.current = null
		}
	}, [color])

	return <>{children}</>
}
